import { useEffect, useRef } from "react";
import { useNewTrialState } from "./NewTrialStateProvider";
import { PLOT_CARD_IDS } from "../../../shared/layout/PlotCard/constants";
import { transformFromBackendFormat } from "../../../shared/layout/PlotCard/utils/optimizeStripsData";
import { resolveStripAbLineIndex } from "../../../shared/layout/PlotCard/utils/resolveStripAbLine";
import {
  clearStripLinesCache,
  clearStripLinesFromMap,
} from "../../../maps/components/MapComponent/utils";

/**
 * Rebuilds `newTrialState` (machinery grid, buffer zone, AB lines, trial plots
 * and strip design) from a saved trial.
 *
 * The API strips client-only bookkeeping keys — `id`, `visible`, `key`,
 * `parentId`, `name` — off every Plot Design entity before it goes on the wire
 * (see `plotDesignPayload.removeKeys`), and flattens `buffer_zone` /
 * `machinery_grid` from a one-element array to a single object. This puts them
 * back, so the loaded trial is indistinguishable from one the user just built.
 *
 * This used to live inside `PlotCard`, which meant the only way to rehydrate a
 * saved trial was to render the Plot Design step. Any other screen that draws
 * the trial — the standalone Rates & Dosage route, which is entered directly by
 * URL — got an empty `newTrialState` and therefore a map with no plots, AB
 * lines or strips on it. It is a hook so both entry points share one mapping.
 *
 * @param trialData the trial as returned by `useTrial`, or undefined while it
 *   is still loading. Hydration runs once per trial.
 */
const useHydratePlotDesign = (trialData: any): void => {
  const { setNewTrialState } = useNewTrialState();

  // Identifies the trial being hydrated. Prefer its id, but fall back to the
  // object itself: a trial can reach this hook before its id is known (and the
  // wizard's own callers pass partial trial payloads), and refusing to hydrate
  // those would leave the map empty.
  const trialKey: unknown = trialData?.id ?? trialData;

  // Hydration must not re-run on every render of the owning component, and
  // must never clobber edits the user has since made in the UI.
  const hydratedKey = useRef<unknown>(undefined);
  const hydratedStripsKey = useRef<unknown>(undefined);

  useEffect(() => {
    if (!trialData || hydratedKey.current === trialKey) {
      return;
    }
    hydratedKey.current = trialKey;

    if (trialData?.machinery_grid) {
      const machineryGridData = {
        ...trialData.machinery_grid,
        key: "Machinery Grid",
        name: "Machinery Grid",
        visible: true,
        id: PLOT_CARD_IDS.MACHINERY_GRID,
      };

      setNewTrialState((prevState: any) => ({
        ...prevState,
        machinery_grid: [machineryGridData],
      }));
    }

    if (trialData?.buffer_zone) {
      const bufferZoneData = {
        ...trialData.buffer_zone,
        key: "Buffer Zone",
        name: "Buffer Zone",
        visible: true,
        id: PLOT_CARD_IDS.BUFFER_ZONE,
      };

      setNewTrialState((prevState: any) => ({
        ...prevState,
        buffer_zone: [bufferZoneData],
      }));
    }

    if (Array.isArray(trialData?.ab_line)) {
      // Restore which AB line the strips were designed on. `selectedForStrips`
      // is stripped from every AB line on save, so it's never present here; the
      // real selection survives only as strip_design.selected_ab_line_index (the
      // same index the strips effect below uses). Resolve that index ONCE, up
      // front, so a strip design built on a non-first AB line isn't silently
      // reset to index 0 — which would make the strip capacity check validate
      // against the wrong AB line's direction and reject counts that fit fine.
      const targetIndex = resolveStripAbLineIndex(
        trialData.ab_line,
        trialData?.strip_design?.selected_ab_line_index
      );

      const abLineData = trialData.ab_line.map((item: any, index: number) => ({
        ...item,
        key: "A/B Line + Machinery Grid",
        visible: true,
        // Single selection: exactly the AB line the strips were designed on.
        selectedForStrips: index === targetIndex,
        id: Number.parseInt(`${PLOT_CARD_IDS.AB_LINE_BASE}${index + 1}`),
      }));

      setNewTrialState((prevState: any) => ({
        ...prevState,
        ab_line: abLineData,
      }));
    }

    if (Array.isArray(trialData?.trial_plots)) {
      const trialPlotData = trialData.trial_plots.map(
        (item: any, index: number) => {
          const updatedReplicants =
            item.replicants?.map((replicant: any, repIndex: number) => ({
              ...replicant,
              parentId: item?.plot?.id || null,
              id: Number.parseInt(
                `${PLOT_CARD_IDS.TRIAL_PLOTS_BASE}${index + 1}${repIndex + 1}`
              ),
              visible: true,
              key: "trial_plots",
              name: `Replicant ${repIndex + 1}`,
            })) || [];

          return {
            ...item,
            plot: {
              ...item.plot,
              key: "Trial Plots",
              visible: true,
              id: Number.parseInt(
                `${PLOT_CARD_IDS.TRIAL_PLOTS_BASE}${index + 1}`
              ),
            },
            replicants: updatedReplicants,
          };
        }
      );

      setNewTrialState((prevState: any) => ({
        ...prevState,
        trial_plots: trialPlotData,
      }));
    }
  }, [trialKey, trialData, setNewTrialState]);

  // Strips arrive as a separate `strip_design` block and are converted to the
  // in-app strips model. Kept in its own effect (and its own guard) because it
  // depends on the AB lines resolved above and is skipped entirely for
  // non-strip trials.
  useEffect(() => {
    if (!trialData || hydratedStripsKey.current === trialKey) {
      return;
    }
    if (!trialData?.strip_design || !trialData?.ab_line) {
      return;
    }
    hydratedStripsKey.current = trialKey;

    try {
      const frontendStripsData = transformFromBackendFormat(
        { strip_design: trialData.strip_design },
        trialData.ab_line
      );

      setNewTrialState((prevState: any) => ({
        ...prevState,
        stripsData: {
          ...frontendStripsData,
          appliedFromPopup: true, // treat backend-loaded strips as already applied
        },
        // Update AB-line selection based on strips data
        ab_line:
          prevState.ab_line?.map((abLine: any, index: number) => ({
            ...abLine,
            selectedForStrips: index === frontendStripsData.selectedAbLineIndex,
          })) || [],
      }));

      // Clear caches to ensure fresh calculation
      clearStripLinesCache();
      clearStripLinesFromMap();
    } catch (error) {
      console.error("❌ Failed to convert backend strips data:", error);
    }
  }, [trialKey, trialData, setNewTrialState]);
};

export default useHydratePlotDesign;
